export interface ShareableConfiguration {
  selectedMain: string;
  selectedOptions: string[];
  totalCost: number;
  totalHours: number;
  timestamp: number;
  // Discount information (optional for backwards compatibility)
  discountEnabled?: boolean;
  discountValue?: number;
  subtotal?: number;
  discountAmount?: number;
}

export class ShareService {
  private static readonly BASE_URL = window.location.origin;
  private static readonly SHARE_PATH = '/share';

  /**
   * Generates a shareable URL with the current configuration
   */
  public static generateShareUrl(config: ShareableConfiguration): string {
    try {
      // Encode the configuration as base64
      const configJson = JSON.stringify(config);
      const encodedConfig = btoa(configJson);
      
      // Create shareable URL
      const shareUrl = `${this.BASE_URL}${this.SHARE_PATH}?config=${encodedConfig}`;
      
      return shareUrl;
    } catch (error) {
      console.error('Error generating share URL:', error);
      throw new Error('Failed to generate share URL');
    }
  }

  /**
   * Decodes configuration from URL parameters
   */
  public static decodeConfigurationFromUrl(): ShareableConfiguration | null {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const encodedConfig = urlParams.get('config');
      
      if (!encodedConfig) {
        return null;
      }

      // Decode the configuration
      const configJson = atob(encodedConfig);
      const config = JSON.parse(configJson) as ShareableConfiguration;
      
      // Validate the configuration
      if (this.isValidConfiguration(config)) {
        return config;
      } else {
        console.error('Invalid configuration data');
        return null;
      }
    } catch (error) {
      console.error('Error decoding configuration:', error);
      return null;
    }
  }

  /**
   * Validates if the configuration object is valid
   */
  private static isValidConfiguration(config: any): config is ShareableConfiguration {
    return (
      config &&
      typeof config.selectedMain === 'string' &&
      Array.isArray(config.selectedOptions) &&
      typeof config.totalCost === 'number' &&
      typeof config.totalHours === 'number' &&
      typeof config.timestamp === 'number'
    );
  }

  /**
   * Copies URL to clipboard
   */
  public static async copyToClipboard(url: string): Promise<boolean> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        return true;
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        return success;
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      return false;
    }
  }

  /**
   * Generates a QR code URL for the shareable link
   */
  public static generateQRCodeUrl(shareUrl: string): string {
    const encodedUrl = encodeURIComponent(shareUrl);
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodedUrl}`;
  }

  /**
   * Formats the configuration for display
   */
  public static formatConfigurationForDisplay(config: ShareableConfiguration): string {
    const date = new Date(config.timestamp).toLocaleDateString('de-DE');
    return `Konfiguration vom ${date} - €${config.totalCost} (${config.totalHours}h)`;
  }
}


